import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, makeStyles } from '@material-ui/core';

import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import { DEFAULT_ROWS_PER_PAGE } from '../../../shared/constants/table.constants';
import { MIN_SEARCH_LENGTH } from '../../../shared/constants/search.constants';
import { EntityStatusFilter, toEntityListFilters } from '../../../shared/constants/entity-status-filter.constants';
import { useAdminGetDeliveryPoints } from '../../hooks/admin-get-delivery-points.hook';
import { useCreateDeliveryPoint } from '../../hooks/create-delivery-point.hook';
import { ADMIN_DELIVERY_POINT_APP_PATHS } from '../../constants/delivery-points-app-paths.constants';
import {
  ALL_DELIVERY_POINT_SEGMENTS,
  ALL_DELIVERY_POINT_TYPES,
  DeliveryPointTypeFilter,
} from '../../constants/delivery-point-type.constants';
import { DeliveryPointSortColumn, DeliveryPointSortDirection } from '../../types/delivery-point.type';
import { CreateDeliveryPointRequestDTO } from '../../types/dtos/create-delivery-point.dto';
import { DeliveryPointsListFilters } from '../../components/Containers/DeliveryPointsListFilters.component';
import { DeliveryPointsTable } from '../../components/Tables/DeliveryPointsTable.component';
import { CreateDeliveryPointDialog } from '../../components/Modals/CreateDeliveryPointDialog.component';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },

  createButton: {
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export default function AdminDeliveryPointsListView() {
  const classes = useStyles();
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<DeliveryPointTypeFilter>(ALL_DELIVERY_POINT_TYPES);
  const [segmentFilter, setSegmentFilter] = useState<string>(ALL_DELIVERY_POINT_SEGMENTS);
  const [statusFilter, setStatusFilter] = useState<EntityStatusFilter>('all');
  const [sortBy, setSortBy] = useState<DeliveryPointSortColumn>('name');
  const [sortDirection, setSortDirection] = useState<DeliveryPointSortDirection>('asc');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 400).trim();

  const { isLoading, isRefetching, totalCount, deliveryPoints } = useAdminGetDeliveryPoints({
    page: page + 1,
    limit: rowsPerPage,
    sortBy,
    sortDirection,
    filters: {
      ...(typeFilter !== ALL_DELIVERY_POINT_TYPES ? { type: typeFilter } : {}),
      ...(segmentFilter !== ALL_DELIVERY_POINT_SEGMENTS ? { segment: segmentFilter } : {}),
      ...(debouncedSearch.length >= MIN_SEARCH_LENGTH ? { name: debouncedSearch } : {}),
      ...toEntityListFilters(statusFilter),
    },
  });
  const { createDeliveryPoint, isSubmitting: isCreating } = useCreateDeliveryPoint();

  const withPageReset =
    <Value,>(setValue: (value: Value) => void) =>
    (value: Value) => {
      setValue(value);
      setPage(0);
    };

  const handleSortChange = (column: DeliveryPointSortColumn) => {
    setSortDirection(column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc');
    setSortBy(column);
    setPage(0);
  };

  const handleCreateDeliveryPoint = async (data: CreateDeliveryPointRequestDTO) => {
    const createdDeliveryPoint = await createDeliveryPoint(data);
    if (!createdDeliveryPoint) return;

    setIsCreateDialogOpen(false);
    history.push(ADMIN_DELIVERY_POINT_APP_PATHS.DELIVERY_POINT_DETAILS_PATH(createdDeliveryPoint.id));
  };

  return (
    <section className={classes.root}>
      <h2>Points de livraison</h2>
      <div className={classes.toolbar}>
        <DeliveryPointsListFilters
          search={search}
          typeFilter={typeFilter}
          segmentFilter={segmentFilter}
          statusFilter={statusFilter}
          onSearchChange={withPageReset(setSearch)}
          onTypeFilterChange={withPageReset(setTypeFilter)}
          onSegmentFilterChange={withPageReset(setSegmentFilter)}
          onStatusFilterChange={withPageReset(setStatusFilter)}
        />
        <Button
          size="small"
          variant="contained"
          className={classes.createButton}
          onClick={() => setIsCreateDialogOpen(true)}
        >
          Créer un point de livraison
        </Button>
      </div>
      <DeliveryPointsTable
        deliveryPoints={deliveryPoints}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        isLoading={isLoading || isRefetching}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onPageChange={setPage}
        onRowsPerPageChange={withPageReset(setRowsPerPage)}
        onSortChange={handleSortChange}
      />
      <CreateDeliveryPointDialog
        open={isCreateDialogOpen}
        isSubmitting={isCreating}
        onSubmit={handleCreateDeliveryPoint}
        onClose={() => setIsCreateDialogOpen(false)}
      />
    </section>
  );
}
